import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Avatar } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CountryFlag from 'react-country-flag';
import lookup from 'country-code-lookup';
import { CUSTOM_FLAGS } from '../events/team-crest';
import { useAuth } from '../../hooks/useAuth';
import { getBetHistory } from '../../services/event-services';

const ORANGE = '#ff9800';
const AMBER = '#ffc107';
const LINE = '1px solid rgba(255,255,255,0.08)';
const LINE2 = '1px solid rgba(255,255,255,0.14)';

const shortCode = (name) =>
  lookup.byCountry(name)?.iso3 || (name || '').slice(0, 3).toUpperCase();

const initials = (u) => (u?.username ? u.username.slice(0, 2).toUpperCase() : '??');
const avatarUrl = (u) => {
  const p = u?.profile?.image;
  if (!p) return null;
  return p.startsWith('http') ? p : `${process.env.REACT_APP_API_URL_BASE}${p}`;
};

function FlagImg({ name, size = 20 }) {
  const cc = lookup.byCountry(name);
  const custom = name ? CUSTOM_FLAGS[name] : null;
  const style = { width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 };
  if (cc) return <CountryFlag countryCode={cc.iso2} svg style={style} />;
  if (custom) return <img src={custom} alt={name} style={style} />;
  return (
    <Box sx={{ ...style, bgcolor: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: 7, fontWeight: 700, color: '#fff' }}>{(name || '').slice(0, 2).toUpperCase()}</Typography>
    </Box>
  );
}

function HistoryRow({ bet, onClick }) {
  const evt = bet.event;
  const evtTime = DateTime.fromFormat(evt.time, "yyyy-MM-dd'T'HH:mm:ss'Z'", { zone: 'utc' }).toLocal();
  const hasResult = evt.score1 !== null && evt.score1 >= 0 && evt.score2 !== null && evt.score2 >= 0;
  const win = hasResult && bet.points === 3;
  const pointsKnown = hasResult && bet.points !== null && bet.points !== undefined;

  return (
    <Box onClick={onClick} sx={{
      cursor: 'pointer', p: '11px 14px', borderRadius: '12px', mb: 1,
      background: win ? 'rgba(255,152,0,0.08)' : '#1e1e1e',
      border: win ? '1px solid rgba(255,152,0,0.3)' : LINE,
      transition: 'border-color .15s ease',
      '&:hover': { borderColor: 'rgba(255,152,0,0.5)' },
    }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <FlagImg name={evt.team1} />
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{shortCode(evt.team1)}</Typography>
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: hasResult ? '#fff' : '#7a7a7a', fontFamily: '"Archivo", sans-serif', textAlign: 'center', px: 1 }}>
          {hasResult ? `${evt.score1} : ${evt.score2}` : '– : –'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{shortCode(evt.team2)}</Typography>
          <FlagImg name={evt.team2} />
        </Box>
      </Box>

      <Box sx={{ mt: 1, pt: 1, borderTop: LINE, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: 11.5, color: '#b0b0b0', fontWeight: 600 }}>
          {evtTime.toFormat('ccc, dd LLL · HH:mm')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 11, color: '#b0b0b0' }}>Bet</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: win ? AMBER : '#fff', fontFamily: '"Archivo", sans-serif' }}>
            {bet.score1} : {bet.score2}
          </Typography>
          {pointsKnown && (
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.375, px: 0.875, py: '2px', borderRadius: 999,
              fontSize: 11, fontWeight: 800,
              color: win ? '#1a1207' : '#b0b0b0',
              background: win ? AMBER : 'transparent',
              border: win ? 'none' : LINE2,
            }}>
              {win && <EmojiEventsIcon sx={{ fontSize: 12 }} />}
              {bet.points} pt{bet.points === 1 ? '' : 's'}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function MemberHistory() {
  const { authData } = useAuth();
  const { groupId, userId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const member = state?.member || null;

  const [bets, setBets] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getBetHistory(authData.token, userId, groupId)
      .then(setBets)
      .catch(err => setError(err.message || 'Could not load betting history'));
  }, [authData.token, userId, groupId]);

  const totalPoints = bets ? bets.reduce((sum, b) => sum + (b.points || 0), 0) : 0;
  const exactCount = bets ? bets.filter(b => b.points === 3).length : 0;

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
        <Link to={`/details/${groupId}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: ORANGE, background: 'rgba(255,152,0,0.10)', border: '1px solid rgba(255,152,0,0.25)', borderRadius: 20, padding: '5px 12px 5px 6px', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
          Back
        </Link>
      </Box>

      {/* header card */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '14px 16px', borderRadius: '16px', background: '#1e1e1e', border: LINE, mb: 2 }}>
        <Avatar src={avatarUrl(member?.user)} sx={{ width: 46, height: 46, fontSize: 16, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.12)' }}>
          {initials(member?.user)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{member?.user?.username || 'Member'}</Typography>
          <Typography sx={{ fontSize: 12, color: '#b0b0b0' }}>Betting history</Typography>
        </Box>
        {bets && (
          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, justifyContent: 'flex-end' }}>
              <Typography sx={{ fontFamily: '"Archivo", sans-serif', fontWeight: 800, fontSize: 20, color: '#fff' }}>{totalPoints}</Typography>
              <Typography sx={{ fontSize: 11, color: '#b0b0b0', fontWeight: 600 }}>pts</Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: AMBER, fontWeight: 600 }}>{exactCount} exact score{exactCount === 1 ? '' : 's'}</Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', py: 3, color: '#ef5350' }}>
          <WarningAmberIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 13 }}>{error}</Typography>
        </Box>
      )}
      {!error && !bets && (
        <Typography sx={{ fontSize: 13, color: '#7a7a7a', textAlign: 'center', py: 3 }}>Loading…</Typography>
      )}
      {bets && bets.length === 0 && (
        <Typography sx={{ fontSize: 13, color: '#7a7a7a', textAlign: 'center', py: 3 }}>No bets on started matches yet.</Typography>
      )}
      {bets && bets.map(bet => (
        <HistoryRow key={bet.id} bet={bet} onClick={() => navigate(`/event/${bet.event.id}`)} />
      ))}
    </Box>
  );
}
